function seedCalculateDiscountedCents(originalCents, percent) {
  const discountCents = Math.floor((originalCents * percent) / 100);
  return Math.max(originalCents - discountCents, 0);
}

var SeedRecentlyViewed = window.SeedRecentlyViewed || class SeedRecentlyViewed extends HTMLElement {
  connectedCallback() {
    this.storageKey = 'seed_recently_viewed';
    this.cardVersion = 'seed-discount-product-card-v4';
    this.limit = Number(this.dataset.limit || 4);
    this.currentHandle = this.dataset.productHandle;
    this.products = this.readProducts();
    this.moneyFormatter = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: this.dataset.currencyCode || window.Shopify?.currency?.active || 'USD',
      currencyDisplay: 'narrowSymbol',
    });

    if (this.currentHandle) this.rememberCurrentProduct();
    if (this.currentHandle) this.bindProductPageUpdates();

    if (this.limit <= 0) {
      this.setSectionHidden(true);
      return;
    }

    this.renderProducts();
  }

  setSectionHidden(hidden) {
    this.hidden = hidden;
    const section = this.closest('.shopify-section') || this.closest('section');
    if (section) section.hidden = hidden;
  }

  readProducts() {
    try {
      return JSON.parse(window.localStorage.getItem(this.storageKey)) || [];
    } catch (error) {
      return [];
    }
  }

  writeProducts(products) {
    try {
      window.localStorage.setItem(this.storageKey, JSON.stringify(products.slice(0, 12)));
    } catch (error) {
      // Storage can be unavailable in private browsing or restricted browser contexts.
    }
  }

  rememberCurrentProduct() {
    const productData = this.readProductData();
    const selectedVariant = this.getSelectedVariant(productData);
    const currentPriceCents = Number(selectedVariant?.price || this.dataset.productPriceCents || 0);
    const comparePriceCents = Number(selectedVariant?.compare_at_price || this.dataset.productComparePriceCents || 0);
    const liveDiscountPercent = this.getLiveDiscountPercent();
    const discountPercent = Number.isFinite(liveDiscountPercent) && liveDiscountPercent > 0 ? liveDiscountPercent : Number.parseFloat(this.dataset.discountPercent || '0');
    const hasDiscount = discountPercent > 0 && discountPercent < 100 && currentPriceCents > 0;
    const displayPriceCents = hasDiscount ? this.calculateDiscountedCents(currentPriceCents, discountPercent) : currentPriceCents;
    const displayCompareCents = hasDiscount ? currentPriceCents : comparePriceCents;
    const variantImage = this.getVariantImage(selectedVariant, productData);
    const variantItems = this.buildVariantItems(productData, discountPercent, selectedVariant);
    const selectedVariantItem = variantItems.find((variant) => String(variant.id) === String(selectedVariant?.id));

    const product = {
      handle: this.currentHandle,
      title: this.dataset.productTitle,
      url: selectedVariant?.id ? `${this.dataset.productUrl}?variant=${selectedVariant.id}` : this.dataset.productUrl,
      image: variantImage || this.dataset.productImage,
      price: selectedVariantItem?.price || this.formatMoney(displayPriceCents),
      comparePrice: selectedVariantItem?.comparePrice || (displayCompareCents > displayPriceCents ? this.formatMoney(displayCompareCents) : ''),
      available: selectedVariantItem?.available || (selectedVariant ? String(Boolean(selectedVariant.available)) : this.dataset.productAvailable),
      discountPercent: selectedVariantItem?.discountPercent || (hasDiscount ? this.formatPercent(discountPercent) : ''),
      saveLabel: this.dataset.saveLabel || 'Save',
      variantId: selectedVariant?.id ? String(selectedVariant.id) : '',
      variants: variantItems,
      cardHtml: this.getCardHtml(),
      cardVersion: this.cardVersion,
    };
    const nextProducts = this.products.filter((item) => item.handle !== product.handle);
    nextProducts.unshift(product);
    this.products = nextProducts;
    this.writeProducts(nextProducts);
  }

  bindProductPageUpdates() {
    if (this.dataset.recentlyViewedUpdatesBound === 'true') return;
    this.dataset.recentlyViewedUpdatesBound = 'true';

    const refreshRecord = () => {
      window.setTimeout(() => {
        this.products = this.readProducts();
        this.rememberCurrentProduct();
      }, 0);
    };

    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
      subscribe(PUB_SUB_EVENTS.variantChange, refreshRecord);
    }

    document.addEventListener('seed:discount-change', refreshRecord);
    document.addEventListener('change', (event) => {
      if (event.target?.matches?.('form[action*="/cart/add"] [name="id"]')) refreshRecord();
    });

    [120, 600, 1400, 2600].forEach((delay) => window.setTimeout(refreshRecord, delay));

    const productInfo = document.querySelector('product-info');
    if (productInfo) {
      const observer = new MutationObserver(refreshRecord);
      observer.observe(productInfo, {
        attributes: true,
        attributeFilter: ['data-seed-discount-percent'],
        childList: true,
        subtree: true,
      });
    }
  }

  getLiveDiscountPercent() {
    const productInfo = document.querySelector('product-info');
    const dataPercent = Number.parseFloat(productInfo?.dataset.seedDiscountPercent || '');
    if (Number.isFinite(dataPercent) && dataPercent > 0) return dataPercent;

    const badgeText = productInfo?.querySelector('.seed-discount-price__badge')?.textContent || '';
    const matchedPercent = badgeText.match(/(\d+(?:\.\d+)?)/);
    const parsedPercent = Number.parseFloat(matchedPercent?.[1] || '');
    return Number.isFinite(parsedPercent) ? parsedPercent : 0;
  }

  calculateDiscountedCents(originalCents, percent) {
    return seedCalculateDiscountedCents(originalCents, percent);
  }

  readProductData() {
    const dataElement = this.querySelector('[data-recently-viewed-product-json]');
    if (!dataElement) return null;

    try {
      return JSON.parse(dataElement.textContent || '{}');
    } catch (error) {
      return null;
    }
  }

  getCardHtml() {
    const template = this.querySelector('[data-recently-viewed-card-template]');
    return template?.innerHTML?.trim() || '';
  }

  getSelectedVariant(productData) {
    const variants = productData?.variants || [];
    if (!variants.length) return null;

    const params = new URLSearchParams(window.location.search);
    const variantFromUrl = params.get('variant');
    const variantFromForm = document.querySelector('form[action*="/cart/add"] [name="id"]')?.value;
    const variantFromSelectedJson = document.querySelector('variant-selects [data-selected-variant]')?.textContent;
    let parsedSelectedVariantId = '';
    try {
      parsedSelectedVariantId = JSON.parse(variantFromSelectedJson || '{}')?.id || '';
    } catch (error) {
      parsedSelectedVariantId = '';
    }
    const selectedId = variantFromUrl || parsedSelectedVariantId || variantFromForm || this.dataset.productVariantId;

    return variants.find((variant) => String(variant.id) === String(selectedId)) || variants.find((variant) => variant.available) || variants[0];
  }

  getVariantImage(variant, productData) {
    const featuredImage = variant?.featured_image;
    if (featuredImage?.src) return featuredImage.src;
    if (featuredImage) return featuredImage;
    return productData?.featuredImage || '';
  }

  buildVariantItems(productData, discountPercent, selectedVariant) {
    const variants = productData?.variants || [];
    const colorIndex = Number(productData?.colorOptionIndex ?? -1);
    if (!variants.length || colorIndex < 0) return [];

    const variantsByColor = new Map();
    variants.forEach((variant) => {
      const colorValue = variant.options?.[colorIndex];
      if (!colorValue) return;

      const current = variantsByColor.get(colorValue);
      const isSelectedColor = selectedVariant?.options?.[colorIndex] === colorValue;
      const isSelectedVariant = String(selectedVariant?.id || '') === String(variant.id);

      if (!current || isSelectedVariant || (!isSelectedColor && !current.available && variant.available)) {
        variantsByColor.set(colorValue, variant);
      }
    });

    return Array.from(variantsByColor.values())
      .map((variant) => {
        const priceCents = Number(variant.price || 0);
        const compareCents = Number(variant.compare_at_price || 0);
        const hasDiscount = discountPercent > 0 && discountPercent < 100 && priceCents > 0;
        const saleCents = hasDiscount ? this.calculateDiscountedCents(priceCents, discountPercent) : priceCents;
        const displayCompareCents = hasDiscount ? priceCents : compareCents;

        return {
          id: String(variant.id),
          title: variant.options?.[colorIndex] || variant.title || '',
          url: variant.id ? `${this.dataset.productUrl}?variant=${variant.id}` : this.dataset.productUrl,
          image: this.getVariantImage(variant, productData),
          price: this.formatMoney(saleCents),
          comparePrice: displayCompareCents > saleCents ? this.formatMoney(displayCompareCents) : '',
          available: String(Boolean(variant.available)),
          discountPercent: hasDiscount ? this.formatPercent(discountPercent) : '',
          swatch: this.getSwatchColor(variant.options?.[colorIndex] || ''),
        };
      });
  }

  getSwatchColor(value = '') {
    const normalized = String(value).toLowerCase();
    const colorMap = {
      black: '#111111',
      white: '#ffffff',
      yellow: '#ffd400',
      red: '#d71920',
      blue: '#1b63ff',
      green: '#188544',
      orange: '#ff7a00',
      pink: '#f36aa7',
      purple: '#7b3ff2',
      gray: '#8b8f97',
      grey: '#8b8f97',
      silver: '#c9ccd1',
      gold: '#d9a441',
      brown: '#7a4a2a',
      navy: '#17294d',
      beige: '#d8c3a5',
    };

    const matched = Object.keys(colorMap).find((key) => normalized.includes(key));
    return matched ? colorMap[matched] : '';
  }

  formatMoney(cents) {
    const value = Number(cents || 0);
    return this.moneyFormatter.format(value / 100);
  }

  formatPercent(value) {
    const percent = Number.parseFloat(value || '0');
    if (!Number.isFinite(percent)) return '';
    return Number.isInteger(percent) ? String(percent) : percent.toFixed(1);
  }

  renderProducts() {
    const target = this.querySelector('[data-recently-viewed-list]');
    if (!target) return;

    const products = this.products
      .filter((item) => item.handle && item.handle !== this.currentHandle && item.cardHtml)
      .filter((item) => item.cardVersion === this.cardVersion)
      .slice(0, this.limit);
    if (!products.length) {
      target.innerHTML = '';
      this.setSectionHidden(true);
      return;
    }

    this.setSectionHidden(false);
    target.innerHTML = products
      .map((product) => {
        if (product.cardHtml) {
          return `<div data-recently-viewed-seed-card data-recently-viewed-variant-id="${this.escapeAttribute(product.variantId || '')}">${product.cardHtml}</div>`;
        }

        const activeVariant = Array.isArray(product.variants)
          ? product.variants.find((variant) => String(variant.id) === String(product.variantId))
          : null;
        const displayProduct = activeVariant
          ? {
              ...product,
              url: activeVariant.url || product.url,
              image: activeVariant.image || product.image,
              price: activeVariant.price || product.price,
              comparePrice: activeVariant.comparePrice || '',
              available: activeVariant.available || product.available,
              discountPercent: activeVariant.discountPercent || product.discountPercent,
            }
          : product;
        const image = displayProduct.image
          ? `<img src="${this.escapeAttribute(displayProduct.image)}" alt="${this.escapeAttribute(displayProduct.title)}" loading="lazy" decoding="async" fetchpriority="low">`
          : '';
        const comparePrice = `<s class="seed-recent-card__compare"${displayProduct.comparePrice ? '' : ' hidden'}>${this.escapeHtml(displayProduct.comparePrice || '')}</s>`;
        const saveHidden = !displayProduct.discountPercent || displayProduct.available === 'false';
        const saveBadge = `<span class="seed-discount-product-card__badge" data-recently-viewed-save${saveHidden ? ' hidden' : ''}>-${this.escapeHtml(displayProduct.discountPercent || '')}%</span>`;
        const soldOut = displayProduct.available === 'false'
          ? `<span class="seed-discount-product-card__badge seed-discount-product-card__badge--dark" data-recently-viewed-sold-out>${this.escapeHtml(this.dataset.soldOutLabel || 'Sold out')}</span>`
          : `<span class="seed-discount-product-card__badge seed-discount-product-card__badge--dark" data-recently-viewed-sold-out hidden>${this.escapeHtml(this.dataset.soldOutLabel || 'Sold out')}</span>`;
        const swatches = this.renderSwatches(product);

        return `
          <article class="seed-recent-card seed-discount-product-card" data-recently-viewed-card>
            <a class="seed-recent-card__image seed-discount-product-card__media" href="${this.escapeAttribute(displayProduct.url)}">
              ${image}
            </a>
            <div class="seed-recent-card__info seed-discount-product-card__info">
              <h3 class="seed-recent-card__title seed-discount-product-card__title">
                <a href="${this.escapeAttribute(displayProduct.url)}">${this.escapeHtml(displayProduct.title)}</a>
              </h3>
              <div class="seed-recent-card__price-row seed-discount-product-card__price-row">
                <span class="seed-recent-card__price">${this.escapeHtml(displayProduct.price)}</span>
                ${comparePrice}
                ${saveBadge}
                ${soldOut}
              </div>
              ${swatches}
            </div>
          </article>
        `;
      })
      .join('');

    if (window.seedDiscountCardsBind) window.seedDiscountCardsBind(target);
    this.activateSeedCards(target);
    this.bindSwatches(target);
  }

  activateSeedCards(target) {
    target.querySelectorAll('[data-recently-viewed-seed-card]').forEach((wrapper) => {
      const variantId = wrapper.dataset.recentlyViewedVariantId;
      const card = wrapper.querySelector('[data-seed-discount-card]');
      if (!card || !variantId) return;

      const variantButton = Array.from(card.querySelectorAll('[data-seed-card-variant]'))
        .find((button) => String(button.dataset.variantId) === String(variantId));
      if (variantButton && !variantButton.classList.contains('is-selected')) {
        variantButton.click();
      }
    });
  }

  renderSwatches(product) {
    const variants = Array.isArray(product.variants) ? product.variants : [];
    if (variants.length <= 1) return '';

    const buttons = variants
      .map((variant) => {
        const selected = String(variant.id) === String(product.variantId);
        const style = variant.swatch ? ` style="--seed-card-swatch: ${this.escapeAttribute(variant.swatch)};"` : '';
        return `
          <button
            type="button"
            class="seed-discount-product-card__swatch${selected ? ' is-selected' : ''}${variant.available === 'false' ? ' is-disabled' : ''}"
            aria-label="${this.escapeAttribute(variant.title)}"
            aria-pressed="${selected ? 'true' : 'false'}"
            data-recently-viewed-variant
            data-variant-id="${this.escapeAttribute(variant.id)}"
            data-variant-url="${this.escapeAttribute(variant.url)}"
            data-variant-image="${this.escapeAttribute(variant.image || '')}"
            data-variant-price="${this.escapeAttribute(variant.price || '')}"
            data-variant-compare="${this.escapeAttribute(variant.comparePrice || '')}"
            data-variant-discount-percent="${this.escapeAttribute(variant.discountPercent || '')}"
            data-variant-available="${this.escapeAttribute(variant.available || 'true')}"
          >
            ${
              variant.swatch
                ? `<span class="seed-discount-product-card__swatch-visual"${style}></span>`
                : `<span class="seed-discount-product-card__swatch-text">${this.escapeHtml(variant.title)}</span>`
            }
          </button>
        `;
      })
      .join('');

    return `<div class="seed-discount-product-card__swatches seed-recent-card__swatches" role="radiogroup">${buttons}</div>`;
  }

  bindSwatches(target) {
    target.querySelectorAll('[data-recently-viewed-card]').forEach((card) => {
      const image = card.querySelector('.seed-recent-card__image img');
      const mediaLink = card.querySelector('.seed-recent-card__image');
      const titleLink = card.querySelector('.seed-recent-card__title a');
      const price = card.querySelector('.seed-recent-card__price');
      const compare = card.querySelector('.seed-recent-card__compare');
      const save = card.querySelector('[data-recently-viewed-save]');
      const soldOut = card.querySelector('[data-recently-viewed-sold-out]');
      const buttons = Array.from(card.querySelectorAll('[data-recently-viewed-variant]'));

      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          buttons.forEach((candidate) => {
            const selected = candidate === button;
            candidate.classList.toggle('is-selected', selected);
            candidate.setAttribute('aria-pressed', selected ? 'true' : 'false');
          });

          if (image && button.dataset.variantImage) image.src = button.dataset.variantImage;
          if (mediaLink && button.dataset.variantUrl) mediaLink.href = button.dataset.variantUrl;
          if (titleLink && button.dataset.variantUrl) titleLink.href = button.dataset.variantUrl;
          if (price && button.dataset.variantPrice) price.textContent = button.dataset.variantPrice;
          if (compare) {
            compare.textContent = button.dataset.variantCompare || '';
            compare.hidden = !button.dataset.variantCompare;
          }

          const variantAvailable = button.dataset.variantAvailable !== 'false';
          if (save) {
            save.hidden = !variantAvailable || !button.dataset.variantDiscountPercent;
            save.textContent = `-${button.dataset.variantDiscountPercent || ''}%`;
          }
          if (soldOut) soldOut.hidden = variantAvailable;
        });
      });
    });
  }

  escapeHtml(value = '') {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  escapeAttribute(value = '') {
    return this.escapeHtml(value);
  }
};
window.SeedRecentlyViewed = SeedRecentlyViewed;

var SeedStickyAddToCart = window.SeedStickyAddToCart || class SeedStickyAddToCart extends HTMLElement {
  connectedCallback() {
    this.mainButton = document.querySelector('[id^="ProductSubmitButton-"]');
    this.bar = this;
    this.observerTarget = this.mainButton?.closest('.product-form__buttons') || this.mainButton;
    this.stickyButton = this.querySelector('[type="submit"]');
    this.stickyButtonText = this.stickyButton?.querySelector('span');
    this.stickyVariantInput = this.querySelector('.product-variant-id');
    this.stickyPrice = this.querySelector('[data-sticky-price]');

    if (!this.mainButton || !this.observerTarget || !this.stickyButton || !this.stickyVariantInput) {
      this.hidden = true;
      return;
    }

    this.setupVisibilityObserver();
    this.setupVariantSync();
    this.syncFromMainProduct();
  }

  setupVisibilityObserver() {
    this.observer = new IntersectionObserver(
      ([entry]) => {
        this.bar.classList.toggle('is-visible', !entry.isIntersecting);
      },
      { threshold: 0.2 }
    );
    this.observer.observe(this.observerTarget);
  }

  setupVariantSync() {
    if (typeof subscribe === 'function' && window.PUB_SUB_EVENTS) {
      this.variantUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, () => this.syncFromMainProduct());
    }
    this.handleDocumentChange = (event) => {
      if (event.target?.matches('.product-variant-id')) this.syncFromMainProduct();
    };
    document.addEventListener('change', this.handleDocumentChange);

    this.mutationObserver = new MutationObserver(() => this.syncFromMainProduct());
    this.mutationObserver.observe(this.mainButton, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled'],
      childList: true,
      subtree: true,
    });

    const mainPrice = document.querySelector('main [id^="price-"]');
    if (mainPrice) {
      this.priceObserver = new MutationObserver(() => this.syncPrice(mainPrice));
      this.priceObserver.observe(mainPrice, { childList: true, subtree: true });
    }
  }

  syncFromMainProduct() {
    const sourceInput = document.querySelector('main .product__info-container form[id^="product-form-"] .product-variant-id');
    if (sourceInput) {
      this.stickyVariantInput.value = sourceInput.value;
      this.stickyVariantInput.disabled = sourceInput.disabled;
    }

    const isDisabled = this.mainButton.disabled || this.mainButton.getAttribute('aria-disabled') === 'true';
    this.stickyButton.disabled = isDisabled;
    this.stickyButton.toggleAttribute('aria-disabled', isDisabled);

    const mainButtonText = this.mainButton.querySelector('span:not(.sold-out-message)');
    if (mainButtonText && this.stickyButtonText) {
      this.stickyButtonText.textContent = isDisabled ? mainButtonText.textContent.trim() : this.stickyButton.dataset.availableLabel;
    }

    const mainPrice = document.querySelector('main [id^="price-"]');
    if (mainPrice) this.syncPrice(mainPrice);
  }

  syncPrice(mainPrice) {
    if (!this.stickyPrice) return;
    const price = mainPrice.querySelector('.price');
    if (price) this.stickyPrice.innerHTML = price.outerHTML;
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.mutationObserver?.disconnect();
    this.priceObserver?.disconnect();
    this.variantUnsubscriber?.();
    if (this.handleDocumentChange) document.removeEventListener('change', this.handleDocumentChange);
  }
};
window.SeedStickyAddToCart = SeedStickyAddToCart;

if (!window.seedDiscountCardsBind) {
  window.seedDiscountCardsBind = (container = document) => {
    container.querySelectorAll('[data-seed-discount-card]').forEach((card) => {
      if (card.dataset.seedDiscountCardBound === 'true') return;
      card.dataset.seedDiscountCardBound = 'true';

      const image = card.querySelector('[data-seed-card-image]');
      const mediaLink = card.querySelector('.seed-discount-product-card__media');
      const titleLink = card.querySelector('.seed-discount-product-card__title a');
      const hoverMainClone = card.querySelector('[data-seed-hover-main-clone]');
      const price = card.querySelector('[data-seed-card-price]');
      const compare = card.querySelector('[data-seed-card-compare]');
      const priceBox = card.querySelector('.seed-discount-product-card__price');
      const saleBadge = card.querySelector('[data-seed-card-sale-badge]');
      const soldOutBadge = card.querySelector('[data-seed-card-sold-out-badge]');
      const savePercent = card.querySelector('[data-seed-card-save-percent]');
      const addButton = card.querySelector('[data-seed-card-add-to-cart]');
      const hoverImages = Array.from(card.querySelectorAll('[data-seed-hover-image]'));
      const buttons = Array.from(card.querySelectorAll('[data-seed-card-variant]'));

      let hoverFrame = 0;
      let hoverResetTimer;
      let hoverPointerFrame = 0;
      let hoverPointerClientX = 0;

      const clearHoverClasses = () => {
        card.classList.remove('is-seed-hover-wrap-next', 'is-seed-hover-resetting');
      };

      const setCarouselFrame = (frame) => {
        if (mediaLink) mediaLink.style.setProperty('--seed-hover-frame', String(frame));
      };

      const getHoverImageCount = () =>
        hoverImages.filter((hoverImage) => !hoverImage.hidden && hoverImage.style.getPropertyValue('--seed-hover-image')).length;

      const syncHoverTrack = () => {
        if (!mediaLink) return 0;
        const imageCount = getHoverImageCount();
        mediaLink.style.setProperty('--seed-hover-frame-count', String(Math.max(1, imageCount + 1)));
        return imageCount;
      };

      const finishHoverReset = () => {
        window.clearTimeout(hoverResetTimer);
        if (!mediaLink) return;

        hoverResetTimer = window.setTimeout(() => {
          if (hoverFrame !== 0 || !mediaLink) return;
          mediaLink.classList.add('is-seed-hover-jump');
          card.classList.add('is-seed-hover-resetting');
          setCarouselFrame(0);
          window.requestAnimationFrame(() => {
            clearHoverClasses();
            window.requestAnimationFrame(() => mediaLink.classList.remove('is-seed-hover-jump'));
          });
          delete card.dataset.seedHoverIndex;
          delete card.dataset.seedHoverPrevious;
          delete card.dataset.seedHoverDirection;
        }, 520);
      };

      const clearHoverState = () => {
        if (hoverPointerFrame) {
          window.cancelAnimationFrame(hoverPointerFrame);
          hoverPointerFrame = 0;
        }

        const imageCount = getHoverImageCount();
        if (mediaLink && imageCount > 0) {
          const maxFrame = Math.min(3, imageCount);
          window.clearTimeout(hoverResetTimer);
          const exitFrame = hoverFrame;

          if (exitFrame >= maxFrame && maxFrame > 1) {
            mediaLink.classList.add('is-seed-hover-jump');
            card.classList.add('is-seed-hover-wrap-next');
            setCarouselFrame(maxFrame);
            card.dataset.seedHoverDirection = 'forward';
            window.requestAnimationFrame(() => {
              mediaLink.classList.remove('is-seed-hover-jump');
              setCarouselFrame(maxFrame + 1);
            });
          } else {
            setCarouselFrame(0);
            card.dataset.seedHoverDirection = exitFrame > 0 ? 'reverse' : 'forward';
          }

          hoverFrame = 0;
          card.dataset.seedHoverIndex = '0';
          card.dataset.seedHoverPrevious = String(Math.min(maxFrame, Math.max(0, exitFrame)));
          finishHoverReset();
          return;
        }

        hoverFrame = 0;
        clearHoverClasses();
        delete card.dataset.seedHoverPrevious;
        delete card.dataset.seedHoverDirection;
        delete card.dataset.seedHoverIndex;
        if (mediaLink) {
          setCarouselFrame(0);
          mediaLink.style.setProperty('--seed-hover-direction', 'forward');
        }
      };

      const setHoverFrame = (nextFrame) => {
        const imageCount = syncHoverTrack();
        if (!mediaLink || imageCount <= 0) {
          clearHoverState();
          return;
        }

        const maxFrame = Math.min(3, imageCount);
        const clampedFrame = Math.max(0, Math.min(maxFrame, nextFrame));
        if (clampedFrame === hoverFrame) return;

        const previousFrame = hoverFrame;
        const fromMainToLast = previousFrame === 0 && clampedFrame === maxFrame && maxFrame > 1;
        window.clearTimeout(hoverResetTimer);
        clearHoverClasses();

        if (fromMainToLast) {
          mediaLink.classList.add('is-seed-hover-jump');
          card.classList.add('is-seed-hover-wrap-next');
          setCarouselFrame(maxFrame + 1);
          window.requestAnimationFrame(() => {
            mediaLink.classList.remove('is-seed-hover-jump');
            setCarouselFrame(maxFrame);
          });
        } else {
          setCarouselFrame(clampedFrame);
        }

        hoverFrame = clampedFrame;
        mediaLink.style.setProperty('--seed-hover-direction', clampedFrame > previousFrame ? 'forward' : 'reverse');
        card.dataset.seedHoverIndex = String(clampedFrame);
        card.dataset.seedHoverPrevious = String(previousFrame);
        card.dataset.seedHoverDirection = clampedFrame > previousFrame ? 'forward' : 'reverse';
      };

      const getFrameFromPointer = (clientX) => {
        if (!mediaLink) return 0;
        const rect = mediaLink.getBoundingClientRect();
        if (!rect.width) return hoverFrame;
        const progress = Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
        if (progress < 1 / 3) return Math.min(1, getHoverImageCount());
        if (progress < 2 / 3) return Math.min(2, getHoverImageCount());
        return Math.min(3, getHoverImageCount());
      };

      const queueHoverFrame = (event) => {
        hoverPointerClientX = event.clientX;
        if (hoverPointerFrame) return;

        hoverPointerFrame = window.requestAnimationFrame(() => {
          hoverPointerFrame = 0;
          setHoverFrame(getFrameFromPointer(hoverPointerClientX));
        });
      };

      const setHoverImages = (images = []) => {
        hoverImages.forEach((hoverImage, index) => {
          const imageUrl = images[index] || '';
          if (imageUrl) {
            hoverImage.style.setProperty('--seed-hover-image', `url('${imageUrl}')`);
            hoverImage.dataset.seedHoverVariantImage = 'true';
            hoverImage.hidden = false;
          } else {
            hoverImage.style.removeProperty('--seed-hover-image');
            delete hoverImage.dataset.seedHoverVariantImage;
            hoverImage.hidden = true;
          }
        });
        syncHoverTrack();
        clearHoverState();
      };

      if (mediaLink) {
        syncHoverTrack();
        setCarouselFrame(0);
        mediaLink.style.setProperty('--seed-hover-direction', 'forward');
        const supportsFineHover = !window.matchMedia || window.matchMedia('(hover: hover) and (pointer: fine)').matches;
        if (supportsFineHover) {
          mediaLink.addEventListener('pointerenter', queueHoverFrame);
          mediaLink.addEventListener('pointermove', queueHoverFrame);
        }
        mediaLink.addEventListener('pointerleave', clearHoverState);
        mediaLink.addEventListener('focus', () => {
          if (syncHoverTrack() > 0) setHoverFrame(1);
        });
        mediaLink.addEventListener('blur', clearHoverState);
      }

      addButton?.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (addButton.disabled || addButton.getAttribute('aria-disabled') === 'true') return;

        const variantId = addButton.dataset.variantId || card.dataset.variantId;
        if (!variantId) return;

        const cart = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
        const formData = new FormData();

        formData.append('id', variantId);
        formData.append('quantity', '1');

        if (cart?.getSectionsToRender) {
          formData.append('sections', cart.getSectionsToRender().map((section) => section.id));
          formData.append('sections_url', window.location.pathname);
          if (cart.setActiveElement) cart.setActiveElement(document.activeElement);
        }

        const config = typeof fetchConfig === 'function' ? fetchConfig('javascript') : { method: 'POST', headers: { Accept: 'application/javascript' } };
        config.headers = config.headers || {};
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];
        config.body = formData;

        addButton.classList.add('is-loading');
        addButton.disabled = true;
        addButton.setAttribute('aria-disabled', 'true');

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              addButton.dataset.seedAddError = response.description || response.message || '';
              return;
            }

            if (cart?.renderContents) cart.renderContents(response);
            else if (window.routes?.cart_url) window.location = window.routes.cart_url;
          })
          .catch((error) => console.error(error))
          .finally(() => {
            addButton.classList.remove('is-loading');
            const isSoldOut = card.dataset.variantSoldOut === 'true';
            addButton.disabled = isSoldOut;
            addButton.setAttribute('aria-disabled', isSoldOut ? 'true' : 'false');
          });
      });

      buttons.forEach((button) => {
        button.addEventListener('click', () => {
          buttons.forEach((candidate) => {
            const selected = candidate === button;
            candidate.classList.toggle('is-selected', selected);
            candidate.setAttribute('aria-pressed', selected ? 'true' : 'false');
          });

          if (image && button.dataset.variantImage) {
            image.src = button.dataset.variantImage;
            if (button.dataset.variantSrcset) image.srcset = button.dataset.variantSrcset;
            if (button.dataset.variantImageWidth) image.width = Number(button.dataset.variantImageWidth);
            if (button.dataset.variantImageHeight) image.height = Number(button.dataset.variantImageHeight);
            if (button.dataset.variantImageAlt) image.alt = button.dataset.variantImageAlt;
            if (hoverMainClone) hoverMainClone.style.setProperty('--seed-hover-main-image', `url('${button.dataset.variantImage}')`);
          }

          if (button.dataset.variantHoverImages) {
            setHoverImages(button.dataset.variantHoverImages.split('||').map((item) => item.trim()).filter(Boolean));
          }

          if (button.dataset.variantUrl) {
            if (mediaLink) mediaLink.href = button.dataset.variantUrl;
            if (titleLink) titleLink.href = button.dataset.variantUrl;
          }

          if (button.dataset.variantId) {
            card.dataset.variantId = button.dataset.variantId;
            if (addButton) addButton.dataset.variantId = button.dataset.variantId;
            card.querySelectorAll('[data-seed-product-card-app-block]').forEach((appBlock) => {
              appBlock.dataset.variantId = button.dataset.variantId;
            });
          }

          if (price && button.dataset.variantPrice) price.textContent = button.dataset.variantPrice;

          const onSale = button.dataset.variantOnSale === 'true';
          const soldOut = button.dataset.variantSoldOut === 'true' || button.dataset.variantAvailable !== 'true';
          const available = !soldOut;
          if (compare) {
            compare.textContent = button.dataset.variantCompare || '';
            compare.hidden = !onSale;
          }

          if (priceBox) {
            priceBox.classList.toggle('price--on-sale', onSale);
            priceBox.dataset.priceCents = button.dataset.variantPriceCents || '';
            priceBox.dataset.comparePriceCents = button.dataset.variantCompareCents || '0';
          }

          card.dataset.variantSoldOut = soldOut ? 'true' : 'false';
          if (addButton) {
            addButton.disabled = soldOut;
            addButton.setAttribute('aria-disabled', soldOut ? 'true' : 'false');
          }

          if (soldOutBadge) soldOutBadge.hidden = true;
          if (saleBadge) saleBadge.hidden = !available || !onSale;
          if (savePercent) savePercent.textContent = button.dataset.variantSavePercent || '0';

          card.dispatchEvent(
            new CustomEvent('seed:discount-card-variant-change', {
              bubbles: true,
              detail: {
                variantId: button.dataset.variantId,
                priceCents: Number(button.dataset.variantPriceCents || 0),
                comparePriceCents: Number(button.dataset.variantCompareCents || 0),
              },
            })
          );
        });
      });
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => window.seedDiscountCardsBind());
  } else {
    window.seedDiscountCardsBind();
  }

  document.addEventListener('shopify:section:load', (event) => window.seedDiscountCardsBind(event.target));
}

if (!customElements.get('seed-recently-viewed')) {
  customElements.define('seed-recently-viewed', SeedRecentlyViewed);
}

if (!customElements.get('seed-sticky-atc')) {
  customElements.define('seed-sticky-atc', SeedStickyAddToCart);
}
